import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Chip,
  Dialog,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import {
  PRIORITIES,
  REQUEST_TYPES,
  addFeatureRequestNote,
  deleteFeatureRequest,
  fetchFeatureRequest,
  fetchFeatureRequests,
  updateFeatureRequestStatus,
} from "./featureRequestsApi";
import { STATUS_META, STATUS_ORDER, statusLabel } from "./featureRequestStatus";
import { sendUserNotification } from "../../utilities";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { isPathGranted } from "../../utils/routeAccess";
import {
  EmptyState,
  LoadingState,
  StatTile,
} from "../../components/UI/States";

export const FEATURE_REQUEST_DASHBOARD_ROLES = ["Admin", "IT"];

const PRIORITY_STYLES = {
  "Blocking my work": "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  "Would help a lot": "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "Nice to have": "bg-slate-500/15 text-slate-700 dark:text-slate-300",
};

const timeAgo = (value) => {
  if (!value) return "";
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
};

const StatusChip = ({ status, size = "small" }) => (
  <Chip
    label={statusLabel(status)}
    color={STATUS_META[status]?.chip || "default"}
    size={size}
  />
);

const PriorityBadge = ({ priority }) => (
  <span
    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      PRIORITY_STYLES[priority] || PRIORITY_STYLES["Nice to have"]
    }`}
  >
    {priority || "Nice to have"}
  </span>
);

// Screenshots are stored as full data URLs; handle raw base64 too.
const imageSrc = (image) => {
  if (!image?.data) return null;
  return image.data.startsWith("data:")
    ? image.data
    : `data:${image.mimeType || "image/jpeg"};base64,${image.data}`;
};

// ---- Screenshot lightbox ----

const ImageLightbox = ({ image, onClose }) => {
  const src = imageSrc(image);
  return (
    <Dialog open={!!image} onClose={onClose} maxWidth="lg">
      {image && (
        <div>
          <div className="flex items-center justify-between gap-4 py-1.5 pl-5 pr-2">
            <p className="truncate text-sm font-medium">
              {image.name || "Screenshot"}
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close screenshot"
              className="app-icon-btn"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <div className="flex items-center justify-center bg-slate-950/90 p-2">
            {src ? (
              <img
                src={src}
                alt={image.name || "Request screenshot"}
                className="max-h-[75vh] max-w-full rounded-lg object-contain"
              />
            ) : (
              <p className="app-muted p-10 text-sm">
                This screenshot has no image data.
              </p>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
};

// ---- Detail view ----

// Notes and status changes share the `notes` array (status entries carry
// type:"status"), so one pass builds the whole timeline.
const buildActivity = (request) =>
  (request?.notes || [])
    .map((entry, index) => ({
      key: String(entry._id || entry.id || `entry-${index}`),
      kind: entry.type === "status" ? "status" : "note",
      text: entry.text || "",
      from: entry.from,
      to: entry.to,
      by: entry.authorName || entry.author?.name || "",
      time: entry.createdAt || entry.timestamp || null,
    }))
    .sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0));

const ActivityEntry = ({ entry }) => {
  if (entry.kind === "status") {
    return (
      <div className="rounded-xl border border-sky-500/25 bg-sky-500/10 p-3 text-xs">
        <p className="font-semibold text-sky-800 dark:text-sky-200">
          ◉ {entry.by || "Someone"} moved this from {statusLabel(entry.from)} to{" "}
          {statusLabel(entry.to)}
        </p>
        <p className="app-muted mt-1 text-[11px]">
          {entry.time ? new Date(entry.time).toLocaleString() : ""}
        </p>
      </div>
    );
  }
  return (
    <div className="app-surface rounded-xl border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="app-accent text-xs font-semibold">
          {entry.by || "Unknown"}
        </p>
        <p className="app-muted shrink-0 text-[11px]">
          {entry.time ? new Date(entry.time).toLocaleString() : ""}
        </p>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-sm">
        {entry.text}
      </p>
    </div>
  );
};

const DetailField = ({ label, children }) => (
  <div>
    <p className="app-eyebrow">{label}</p>
    <div className="mt-1 text-sm font-medium">{children || "—"}</div>
  </div>
);

const RequestDetail = ({ requestId, user, onBack, onChanged, onDeleted }) => {
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    fetchFeatureRequest(requestId, user?.id)
      .then((doc) => {
        setRequest(doc);
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    setRequest(null);
    load();
  }, [requestId]);

  const author = { name: user?.name || "", email: user?.email || "" };

  // In-app notification to the requester (they also get an email from the
  // server) — skipped when triaging your own request.
  const notifyRequester = (subject, message) => {
    const email = request?.requesterEmail;
    if (!email || email === user?.email?.toLowerCase()) return;
    sendUserNotification(
      email,
      user?.name || "HelpDesk",
      message,
      subject,
      "/forms/feature-request"
    );
  };

  const handleStatusChange = async (status) => {
    if (!request || savingStatus || status === request.status) return;
    setSavingStatus(true);
    try {
      await updateFeatureRequestStatus(requestId, status, author, user?.id);
      notifyRequester(
        "Feature request update",
        `Your request "${request.title}" is now ${statusLabel(status)}.`
      );
      load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAddNote = async () => {
    const text = noteDraft.trim();
    if (!text || savingNote) return;
    setSavingNote(true);
    try {
      await addFeatureRequestNote(requestId, text, author, user?.id);
      notifyRequester(
        "Reply on your feature request",
        `${user?.name || "HelpDesk"} replied on "${request?.title}": "${text}"`
      );
      setNoteDraft("");
      load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    if (
      !window.confirm(
        "Delete this request? It disappears from the dashboard — use Declined instead if the requester should still see it."
      )
    )
      return;
    setDeleting(true);
    try {
      await deleteFeatureRequest(requestId, user?.id);
      onDeleted?.();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const activity = buildActivity(request);
  const images = request?.images || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          All Requests
        </Button>
        {request && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="app-muted text-sm" id="fr-status-label">
              Status
            </span>
            <FormControl size="small" className="min-w-[10rem]">
              <Select
                labelId="fr-status-label"
                value={request.status || "new"}
                disabled={savingStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {STATUS_ORDER.map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabel(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              disabled={deleting}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
        >
          {error}
        </div>
      )}

      {!request && !error && (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading request…" className="py-20" />
        </div>
      )}

      {request && (
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Left: the request itself */}
          <div className="space-y-4 lg:col-span-2">
            <div className="app-card rounded-2xl border p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="break-words text-xl font-bold tracking-tight">
                    {request.title}
                  </h2>
                  <p className="app-muted mt-0.5 text-sm">
                    {request.requestType}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <PriorityBadge priority={request.priority} />
                  <StatusChip status={request.status} />
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
                <DetailField label="Requested By">
                  <p>{request.requesterName || "—"}</p>
                  {request.requesterEmail && (
                    <a
                      href={`mailto:${request.requesterEmail}`}
                      className="app-link break-all text-xs font-normal"
                    >
                      {request.requesterEmail}
                    </a>
                  )}
                </DetailField>
                <DetailField label="Page / Area">{request.area}</DetailField>
                <DetailField label="Branch">{request.branch}</DetailField>
                <DetailField label="Submitted">
                  {request.createdAt && (
                    <>
                      <p>{new Date(request.createdAt).toLocaleString()}</p>
                      <p className="app-muted text-xs font-normal">
                        {timeAgo(request.createdAt)}
                      </p>
                    </>
                  )}
                </DetailField>
                {request.pagePath && (
                  <DetailField label="Submitted From">
                    <a
                      href={request.pagePath}
                      className="app-link break-all"
                    >
                      {request.pagePath}
                    </a>
                  </DetailField>
                )}
              </div>

              <div className="app-divider mt-5 border-t pt-4">
                <p className="app-eyebrow">Details</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-sm">
                  {request.description || "—"}
                </p>
              </div>
            </div>

            {/* Screenshots */}
            <div className="app-card rounded-2xl border p-5">
              <h3 className="font-semibold">Screenshots</h3>
              {images.length === 0 ? (
                <p className="app-muted mt-2 text-sm">
                  No screenshots were attached.
                </p>
              ) : (
                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {images.map((image, index) => {
                    const src = imageSrc(image);
                    return (
                      <button
                        key={`${image.name}-${index}`}
                        type="button"
                        onClick={() => setLightboxImage(image)}
                        aria-label={`Open ${image.name || `screenshot ${index + 1}`}`}
                        className="app-surface relative aspect-square overflow-hidden rounded-xl border transition hover:-translate-y-0.5 hover:border-sky-400"
                      >
                        {src ? (
                          <img
                            src={src}
                            alt={image.name || `Screenshot ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="app-surface flex h-full w-full flex-col items-center justify-center">
                            <ImageNotSupportedIcon
                              className="app-muted"
                              fontSize="small"
                            />
                            <span className="app-muted px-2 text-center text-[11px]">
                              {image.name || "Unavailable"}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: activity timeline */}
          <div className="app-card flex max-h-[75vh] min-h-[24rem] flex-col overflow-hidden rounded-2xl border">
            <div className="app-divider border-b px-4 py-3">
              <h3 className="font-semibold">Activity</h3>
              <p className="app-muted mt-0.5 text-[11px]">
                Notes and status changes are emailed to the requester.
              </p>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              {activity.length === 0 && (
                <p className="app-muted pt-8 text-center text-sm">
                  No activity yet — add the first note.
                </p>
              )}
              {activity.map((entry) => (
                <ActivityEntry key={entry.key} entry={entry} />
              ))}
            </div>
            <div className="app-divider flex items-end gap-2 border-t p-3">
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                placeholder="Reply to the requester..."
                inputProps={{ "aria-label": "Reply to the requester" }}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddNote}
                disabled={!noteDraft.trim() || savingNote}
              >
                Post
              </Button>
            </div>
          </div>
        </div>
      )}

      <ImageLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
};

// ---- Stat tiles ----

const STAT_TILES = [
  { key: "new", label: "New", icon: LightbulbOutlinedIcon, tone: "amber" },
  {
    key: "planned",
    label: "Planned",
    icon: PlaylistAddCheckIcon,
    tone: "sky",
  },
  {
    key: "in_progress",
    label: "In Progress",
    icon: AutorenewIcon,
    tone: "violet",
  },
  {
    key: "completed",
    label: "Completed",
    icon: TaskAltIcon,
    tone: "emerald",
  },
  {
    key: "All",
    label: "Total",
    icon: ChatBubbleOutlineIcon,
    tone: "default",
  },
];

// ---- Dashboard ----

const FeatureRequestsDashboard = () => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  // Roles, or a Page Access grant on this page (the API honors it too).
  const canView =
    FEATURE_REQUEST_DASHBOARD_ROLES.some((r) => role?.roles?.includes(r)) ||
    isPathGranted("/admin/feature-requests", role?.grantedPaths);

  // The filter Selects, search field, note box, chips and lightbox Dialog are
  // MUI, which keeps its own palette — feed the in-app toggle into a MUI theme
  // so their text stays legible on the dark cards.
  const muiTheme = useAppMuiTheme();

  // Deep-link straight to a request via ?request=<id>
  const [searchParams, setSearchParams] = useSearchParams();

  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(
    searchParams.get("request") || null
  );

  const load = () => {
    fetchFeatureRequests(user?.id)
      .then((list) => {
        setRequests(list);
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    if (canView && user?.id) load();
  }, [canView, user?.id]);

  const selectRequest = (id) => {
    setSelectedId(id);
    setSearchParams(id ? { request: id } : {}, { replace: true });
  };

  const counts = useMemo(() => {
    const c = { All: (requests || []).length };
    STATUS_ORDER.forEach((s) => (c[s] = 0));
    (requests || []).forEach((r) => {
      if (c[r.status] !== undefined) c[r.status] += 1;
    });
    return c;
  }, [requests]);

  const filtered = (requests || []).filter((r) => {
    if (statusFilter !== "All" && r.status !== statusFilter) return false;
    if (typeFilter !== "All" && r.requestType !== typeFilter) return false;
    if (priorityFilter !== "All" && r.priority !== priorityFilter) return false;
    if (search.trim()) {
      const haystack = [
        r.title,
        r.description,
        r.area,
        r.pagePath,
        r.requesterName,
        r.requesterEmail,
        r.branch,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });

  if (!canView) {
    return (
      <PageShell size="sm">
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={LightbulbOutlinedIcon}
            title="Feature Requests"
            description="You need an Admin or IT role to access this dashboard."
          />
        </div>
      </PageShell>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <PageShell size="2xl">
        <PageHeader
          icon={LightbulbOutlinedIcon}
          tone="amber"
          eyebrow="Admin"
          eyebrowTo="/admin"
          title="Feature Requests"
          description="Features and changes staff have asked for — triage, reply, and track them here."
        />

        <div className="space-y-5">
          {selectedId ? (
            <RequestDetail
              requestId={selectedId}
              user={user}
              onBack={() => selectRequest(null)}
              onChanged={load}
              onDeleted={() => {
                selectRequest(null);
                load();
              }}
            />
          ) : (
            <>
              {error && (
                <div
                  role="alert"
                  className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
                >
                  {error}
                </div>
              )}

              {/* Stat tiles */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {STAT_TILES.map((tile) => (
                  <StatTile
                    key={tile.key}
                    label={tile.label}
                    icon={tile.icon}
                    tone={tile.tone}
                    value={requests === null ? "—" : counts[tile.key]}
                    active={statusFilter === tile.key}
                    onClick={() => setStatusFilter(tile.key)}
                  />
                ))}
              </div>

              {/* Filters */}
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label="Filter by status"
                >
                  {["All", ...STATUS_ORDER].map((s) => {
                    const active = statusFilter === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setStatusFilter(s)}
                        className={`inline-flex min-h-[2.25rem] items-center rounded-full border px-3.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                          active
                            ? "border-transparent bg-sky-600 text-white shadow-sm"
                            : "app-surface text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]"
                        }`}
                      >
                        {s === "All" ? "All" : statusLabel(s)}
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <FormControl size="small" className="w-full sm:w-52">
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      displayEmpty
                      inputProps={{ "aria-label": "Request type" }}
                    >
                      <MenuItem value="All">All Request Types</MenuItem>
                      {REQUEST_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" className="w-full sm:w-44">
                    <Select
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      displayEmpty
                      inputProps={{ "aria-label": "Priority" }}
                    >
                      <MenuItem value="All">All Priorities</MenuItem>
                      {PRIORITIES.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Search title, details, requester..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:w-64"
                    inputProps={{ "aria-label": "Search requests" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>

              {/* Table */}
              <div className="app-card overflow-hidden rounded-2xl border">
                <div className="overflow-x-auto">
                  <table className="app-table">
                    <thead>
                      <tr>
                        <th>Submitted</th>
                        <th>Request</th>
                        <th>Type</th>
                        <th>Area</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>
                          <span className="sr-only">Open</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests === null ? (
                        <tr>
                          <td colSpan={7}>
                            <LoadingState label="Loading requests..." />
                          </td>
                        </tr>
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan={7}>
                            <EmptyState
                              compact
                              icon={
                                requests.length === 0
                                  ? LightbulbOutlinedIcon
                                  : SearchOffIcon
                              }
                              title={
                                requests.length === 0
                                  ? "No requests have been submitted yet."
                                  : "Nothing matches the current filters."
                              }
                            />
                          </td>
                        </tr>
                      ) : (
                        filtered.map((r) => (
                          <tr
                            key={r._id}
                            tabIndex={0}
                            onClick={() => selectRequest(r._id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") selectRequest(r._id);
                            }}
                            className="cursor-pointer focus:outline-none focus-visible:bg-[color:var(--app-hover)]"
                          >
                            <td className="whitespace-nowrap">
                              <div>{timeAgo(r.createdAt)}</div>
                              <div className="app-muted text-xs">
                                {r.requesterName}
                              </div>
                            </td>
                            <td className="max-w-[320px]">
                              <div className="truncate font-medium">
                                {r.title}
                              </div>
                              <div className="app-muted truncate text-xs">
                                {r.description}
                              </div>
                            </td>
                            <td className="whitespace-nowrap">
                              {r.requestType || "—"}
                            </td>
                            <td className="max-w-[180px] truncate">
                              {r.area || r.pagePath || "—"}
                            </td>
                            <td className="whitespace-nowrap">
                              <PriorityBadge priority={r.priority} />
                            </td>
                            <td className="whitespace-nowrap">
                              <StatusChip status={r.status} />
                            </td>
                            <td className="whitespace-nowrap text-right">
                              <span className="app-muted inline-flex items-center gap-2.5">
                                {r.imageCount > 0 && (
                                  <span className="inline-flex items-center gap-1 text-xs tabular-nums">
                                    <PhotoLibraryIcon sx={{ fontSize: 15 }} />
                                    {r.imageCount}
                                  </span>
                                )}
                                {r.noteCount > 0 && (
                                  <span className="inline-flex items-center gap-1 text-xs tabular-nums">
                                    <ChatBubbleOutlineIcon
                                      sx={{ fontSize: 15 }}
                                    />
                                    {r.noteCount}
                                  </span>
                                )}
                                <ChevronRightIcon fontSize="small" />
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </PageShell>
    </ThemeProvider>
  );
};

export default FeatureRequestsDashboard;
